import type { ReactNode } from "react";

import { formatASQ, idFragment } from "@/lib/format";

/**
 * The vocabulary the whole interface is drawn with.
 *
 * These are not wrappers around a component library — there isn't one. Each is
 * a small piece of the drawing-board language from plan §36: coordinate labels,
 * hairline panels with corner marks, status that always carries a word, and the
 * consumption rail that everything else is arranged around.
 */

/* -------------------------------------------------------------------------- */
/* Labels                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A coordinate annotation, as on a drawing sheet.
 *
 * `coordinate` carries real data — a registry id fragment, a version, a
 * count — rather than a decorative sequence number. Two utilities can never
 * share an id fragment, so it is checkable against the chain by eye, which a
 * decorative `01 / 02 / 03` never is.
 */
export function SheetLabel({
  coordinate,
  children,
  tone = "dim",
}: {
  coordinate?: string;
  children: ReactNode;
  tone?: "dim" | "bright";
}) {
  return (
    <p className={`micro ${tone === "bright" ? "micro-bright" : ""} flex items-center gap-2`}>
      {coordinate ? (
        <>
          <span className="text-hub-700">{coordinate}</span>
          <span aria-hidden="true" className="h-px w-4 bg-current opacity-40" />
        </>
      ) : null}
      <span>{children}</span>
    </p>
  );
}

/**
 * Status, with a word.
 *
 * The word is not optional and there is no variant without it — plan §63 asks
 * that status never be colour-only, and the way to guarantee that is to make
 * the colourless version impossible to build.
 */
export function Status({ state, children }: { state: RegistryTone; children: ReactNode }) {
  return (
    <span className={`status status--${state}`}>
      <span>{children}</span>
    </span>
  );
}

export type RegistryTone = "ok" | "warn" | "bad" | "idle";

/** Maps a registry status onto a tone. Anything unrecognised reads as idle
 *  rather than as healthy — an unknown state is not a working one. */
export function toneForStatus(status: string | undefined): RegistryTone {
  switch (status) {
    case "Active":
      return "ok";
    case "Registered":
    case "Pending":
      return "warn";
    case "Deprecated":
    case "Suspended":
    case "Expired":
      return "warn";
    case "Disabled":
    case "Revoked":
      return "bad";
    default:
      return "idle";
  }
}

/* -------------------------------------------------------------------------- */
/* Panels                                                                      */
/* -------------------------------------------------------------------------- */

export function Panel({
  children,
  className = "",
  tone = "board",
  marked = false,
  as: Element = "div",
}: {
  children: ReactNode;
  className?: string;
  tone?: "board" | "paper" | "flat";
  marked?: boolean;
  as?: "div" | "section" | "article" | "li";
}) {
  const toneClass = tone === "paper" ? "panel--paper" : tone === "flat" ? "panel--flat" : "";
  return (
    <Element className={`panel ${toneClass} ${marked ? "panel--marked" : ""} ${className}`}>
      {children}
    </Element>
  );
}

/* -------------------------------------------------------------------------- */
/* The consumption rail                                                        */
/* -------------------------------------------------------------------------- */

/**
 * ASQ entering a vault, drawn.
 *
 * This is the app's signature and it means one specific thing: value travels
 * along the line and reaches a terminal it cannot leave. It appears on the
 * payment sheet, in the transaction stepper, on the dashboard ledger and on the
 * protocol map — always for the same relationship, never as decoration.
 *
 * The terminal block is notched rather than pointed, because an arrowhead
 * suggests onward travel and the entire point is that there is none.
 */
export function ConsumptionRail({
  from,
  to,
  amount,
  tone = "board",
}: {
  from: string;
  to: string;
  amount?: string;
  tone?: "board" | "paper";
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="rail" aria-hidden="true">
        <span className="rail__origin" />
        <span className={`rail__line ${tone === "paper" ? "rail__line--paper" : ""}`} />
        <span className="rail__terminal" />
      </div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="micro">{from}</span>
        {amount ? (
          <span className="datum text-asq-gold text-xs">{amount} ASQ</span>
        ) : null}
        <span className="micro text-right">{to}</span>
      </div>
      <p className="sr-only">
        {amount ? `${amount} ASQ ` : ""}
        berpindah dari {from} ke {to} dan terkunci permanen.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Money                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * An ASQ figure.
 *
 * Gold, tabular, and always followed by the ticker. Gold appears nowhere else
 * in the interface, so a gold number is always an amount of ASQ — which is the
 * whole of plan §36.3's rule about the accent, enforced by there being one
 * component that applies it.
 */
export function ASQAmount({
  raw,
  size = "md",
  maxFractionDigits,
}: {
  raw: string | bigint | undefined | null;
  size?: "sm" | "md" | "lg" | "xl";
  maxFractionDigits?: number;
}) {
  const text = formatASQ(raw, { maxFractionDigits });

  if (size === "xl") {
    return (
      <span className="datum-xl text-asq-gold">
        {text}
        <span className="ml-2 font-mono text-[0.3em] tracking-[0.2em] align-super">ASQ</span>
      </span>
    );
  }

  const sizeClass = size === "lg" ? "text-2xl" : size === "sm" ? "text-xs" : "text-base";
  return (
    <span className={`datum text-asq-gold ${sizeClass}`}>
      {text}
      <span className="ml-1 text-[0.7em] tracking-[0.15em] opacity-70">ASQ</span>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* States                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A loading placeholder shaped like the thing it is standing in for.
 *
 * Deliberately not a shimmer. A pulsing gradient is the SaaS-template tell plan
 * §36.10 warns about; a hairline outline with a hatched fill reads as a drawing
 * that has not been dimensioned yet, which is the same idea in this app's own
 * vernacular.
 */
export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div
      className={`skeleton ${className}`}
      aria-hidden="true"
    />
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Panel className="p-8 text-center" tone="flat">
      <h3 className="display-section text-xl text-paper-100">{title}</h3>
      <p className="mx-auto mt-3 max-w-md text-sm text-paper-300/70">{children}</p>
      {action ? <div className="mt-6 flex justify-center">{action}</div> : null}
    </Panel>
  );
}

/**
 * A refusal, with its machine code shown.
 *
 * The code is on screen on purpose. When a tester reports "it says unavailable"
 * the first question is always *which* unavailable, and `UTILITY_NOT_ACTIVE`
 * against `REGISTRY_UNREADABLE` sends someone to completely different places.
 */
export function ErrorState({
  title,
  code,
  children,
  action,
}: {
  title: string;
  code?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Panel className="p-6" marked>
      <div className="flex flex-wrap items-center gap-3">
        <Status state="bad">{title}</Status>
        {code ? <span className="micro">{code}</span> : null}
      </div>
      <p className="mt-3 max-w-prose text-sm text-paper-300/80">{children}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/* Utility identity                                                            */
/* -------------------------------------------------------------------------- */

/** The readable name for a registry id, for headings and labels. */
export function utilityLabel(name: string | undefined): string {
  switch (name) {
    case "ASETQU_MEMBERSHIP":
      return "Membership";
    case "ASETQU_EVOUCHER":
      return "E-Voucher";
    case "ASETQU_CATALOG":
      return "Catalog";
    default:
      return name ?? "Utility";
  }
}

/**
 * The registry facts for one utility, rendered as a spec block.
 *
 * Shown on every payment screen, which is plan §50: the user sees the utility
 * id, the status, the vault and the irreversibility before they sign, not after.
 */
export function RegistryBlock({
  utility,
}: {
  utility: {
    name: string;
    id: string;
    status: string;
    vault: string;
    module: string;
    vaultUrl?: string;
  };
}) {
  return (
    <dl className="grid gap-3 text-xs sm:grid-cols-2">
      <div>
        <dt className="micro">Utility ID</dt>
        <dd className="hexline mt-1">{utility.name}</dd>
      </div>
      <div>
        <dt className="micro">Registry status</dt>
        <dd className="mt-1">
          <Status state={toneForStatus(utility.status)}>{utility.status}</Status>
        </dd>
      </div>
      <div>
        <dt className="micro">Module</dt>
        <dd className="hexline mt-1 opacity-80">{utility.module}</dd>
      </div>
      <div>
        <dt className="micro">Permanent vault</dt>
        <dd className="hexline mt-1 opacity-80">
          {utility.vaultUrl ? (
            <a
              className="linkline text-hub-700"
              href={utility.vaultUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              {utility.vault}
            </a>
          ) : (
            utility.vault
          )}
        </dd>
      </div>
      <div className="sm:col-span-2">
        <dt className="micro">Coordinate</dt>
        <dd className="datum mt-1 text-xs opacity-70">{idFragment(utility.id)}</dd>
      </div>
    </dl>
  );
}
